import {UserCampaignListItemDto, GigsFilters} from '@/api/gigs/types';
import {CollaborationGigBase, FEE_MODE} from '@/types/gigs';
import {isSocialNetwork} from '@/types/platforms';

export const QUERY_STALE_TIME = 1 * 60 * 1000; // 1 minute

const GIGS_QUERY_KEY = 'gigs';

export interface FilterableGigsQueryOptions {
  enabled?: boolean;
  params?: GigsFilters;
  include?: string[];
}

type FetchedGigsKind = 'available' | 'invited' | 'paid' | 'count';

/**
 * Generates a query key for the gigs query.
 * @param dataType - The type of gigs data we want to fetch.
 * @param params - The parameters to filter the gigs.
 * @param include - The fields to include in the query key.
 * @returns The query key.
 */
export const getGigsQueryKey = (
  dataType: FetchedGigsKind,
  params: object,
  include?: string[]
) => {
  const queryKey = [GIGS_QUERY_KEY, dataType, params];
  if (include && Array.isArray(include) && include.length > 0) {
    queryKey.push(include.join(','));
  }
  return queryKey;
};

/**
 * Maps a UserCampaignListItemDto to a CollaborationGigBase model for use with Suggested Gigs or Invites
 */
export const mapUserCampaignListItemDtoToCollaborationGigBase = (
  campaign: UserCampaignListItemDto
): CollaborationGigBase => {
  const {feeType, feeAmount, feeSuggestedMin, feeSuggestedMax} =
    determineFeeStructure(campaign);
  const profile = campaign.preferredCreatorProfile;

  return {
    id: campaign.id,
    brand: campaign.merchantName,
    campaignImage: campaign.imageUrl,
    campaignName: campaign.name,
    ageRange: profile?.ageDescription || undefined,
    location: profile?.locationDescription || undefined,
    gender: profile?.genderDescription || undefined,
    specialty: profile?.specialtyDescription || undefined,
    capability: profile?.capabilityDescription || undefined,
    unlocksAt: campaign.unlocksAt,
    locksAbove: campaign.locksAbove,
    feeType,
    feeAmount,
    feeSuggestedMin,
    feeSuggestedMax,
    commissionRate: campaign.commissionRate ?? null,
    deliverableCounts: calculateDeliverableCounts(campaign),
    objectivePercent: calculateObjectivePercent(campaign),
    network: isSocialNetwork(campaign.network) ? campaign.network : undefined,
    postRequirements: {
      feed: campaign.requiredFeedPostsCount,
      story: campaign.requiredStoryPostsCount,
      reels: campaign.requiredReelsPostsCount,
    },
  };
};

// Helper function to calculate objectivePercent
const calculateObjectivePercent = (
  campaign: UserCampaignListItemDto
): number => {
  const {objective, objectiveProgress} = campaign;

  if (!objective || !objectiveProgress) {
    return 0;
  }

  const objectiveNum = Number(objective);
  if (isNaN(objectiveNum) || objectiveNum === 0) {
    return 0;
  }

  return Math.floor((objectiveProgress / objectiveNum) * 100);
};

// Helper function to list deliverable counts by type
export const calculateDeliverableCounts = (
  campaign: UserCampaignListItemDto
) => {
  const counts = {
    videos: campaign.videosToUpload,
    photos: campaign.photosToUpload,
    blogs: campaign.blogsToUpload,
    mixedMedia: campaign.mixedMediaToUpload,
    feed: campaign.requiredFeedPostsCount,
    reels: campaign.requiredReelsPostsCount,
    stories: campaign.requiredStoryPostsCount,
  };
  return {
    ...counts,
    total: Object.values(counts).reduce((acc, count) => acc + count, 0),
  };
};

// Helper function to determine fee structure
const determineFeeStructure = (campaign: UserCampaignListItemDto) => {
  const {fixedCost, suggestedBidMin, suggestedBidMax} = campaign;

  let feeType: 'fixed' | 'suggested' | 'bid' | null = null;
  let feeAmount: number | null = null;
  let feeSuggestedMin: number | null = null;
  let feeSuggestedMax: number | null = null;

  if (fixedCost != null && !isNaN(Number(fixedCost)) && Number(fixedCost) > 0) {
    feeType = FEE_MODE.FIXED;
    feeAmount = Number(fixedCost);
  } else if (suggestedBidMin != null || suggestedBidMax != null) {
    feeType = FEE_MODE.SUGGESTED;
    if (Number(suggestedBidMin) > 0) {
      feeSuggestedMin = Number(suggestedBidMin);
    }
    if (Number(suggestedBidMax) > 0) {
      feeSuggestedMax = Number(suggestedBidMax);
    }
  } else {
    feeType = FEE_MODE.BID;
  }

  return {feeType, feeAmount, feeSuggestedMin, feeSuggestedMax};
};
